import {
  GLASS_BLUR,
  GLASS_DEFAULT_TINT,
  GLASS_RADIUS,
  MOTION,
  glassPalette,
  opaqueOver,
  paintableColor,
  withAlpha,
  type GlassPalette,
} from "../shared/appearance";
import {
  DEFAULT_ACCENT_COLOR,
  GLASS_DESKTOP_CARD_WIDTH,
  GLASS_ROW_CLASS,
  GLASS_VARS,
  NOTICE_KEYFRAMES,
} from "./constants";
import { buildLayoutParts } from "./layout-styles";
import { mergeNoticeStyles } from "./variant-styles";
import type {
  ConfirmDialogSkin,
  GlassParts,
  GlassSkinInput,
  LayoutSurface,
  NoticeSkin,
  SelectionControlSkin,
  Style,
  StyleMap,
  StylePatch,
} from "./types";

const blur: Style = {
  backdropFilter: GLASS_BLUR,
  WebkitBackdropFilter: GLASS_BLUR,
};

/** A verification-code field: a translucent control, like the notice's others. */
const otpField = (p: GlassPalette, color: string): Style => ({
  background: p.control,
  border: `1px solid ${p.outline}`,
  borderRadius: GLASS_RADIUS.control,
  color,
});

const stylePatch = (
  p: GlassPalette,
  { isMobile, docked, textColor, headingColor, tint = GLASS_DEFAULT_TINT }: GlassSkinInput,
): StylePatch => ({
  overlay: {
    backgroundColor: p.dim,
    animation: `${NOTICE_KEYFRAMES.fade} ${MOTION.dimMs}ms ${MOTION.outEase} both`,
  },

  // A floating card, centred by translate, which the float keyframes end on.
  // Where it sits (a docked sheet, a corner, a bar) is the layout's
  // `modalPosition`, spread over this.
  modal: {
    borderRadius: GLASS_RADIUS.sheet,
    backgroundColor: p.sheet,
    ...blur,
    border: `1px solid ${p.sheetEdge}`,
    boxShadow: docked ? p.shadow : p.floatShadow,
    maxHeight: "85vh",
    overflow: "hidden",
    animation: `${NOTICE_KEYFRAMES.float} ${MOTION.dimMs}ms ${MOTION.outEase} both`,
  },

  topSection: { backgroundColor: "transparent" },
  headerStack: { backgroundColor: "transparent" },

  title: {
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
  },

  topRight: {
    borderRadius: GLASS_RADIUS.pill,
    height: "32px",
    padding: "0 12px",
    border: `1px solid ${p.hairline}`,
    fontWeight: 600,
    lineHeight: 1,
    backgroundColor: p.control,
  },

  privacyText: { lineHeight: 1.55 },

  link: {
    fontWeight: 600,
    textDecoration: "underline",
    textDecorationThickness: "1px",
    textUnderlineOffset: "3px",
  },

  subTitle: {
    fontWeight: 700,
    margin: "6px 4px 0",
  },

  // The purposes as one grouped panel; rows separate with injected hairlines.
  optionsContainer: {
    gap: 0,
    borderRadius: GLASS_RADIUS.panel,
    backgroundColor: p.panel,
    border: `1px solid ${p.panelEdge}`,
    overflow: "hidden",
    flexShrink: 0,
  },

  optionItem: {
    gap: "12px",
    padding: isMobile ? "12px 14px 12px 10px" : "14px 16px 14px 12px",
  },

  optionLeft: {
    gap: "10px",
    flex: 1,
    minWidth: 0,
    borderRadius: GLASS_RADIUS.row,
  },

  optionTextContainer: { gap: "2px", minWidth: 0 },

  optionTitle: { fontWeight: 700, lineHeight: 1.4, letterSpacing: 0 },

  optionDescription: { lineHeight: 1.45, letterSpacing: 0, opacity: 0.85 },

  sectionHeader: {
    margin: 0,
    padding: isMobile ? "14px 14px 10px 10px" : "16px 16px 10px 12px",
  },

  // Purposes sit under their product with a small indent and no gap: the
  // panel's hairlines already separate them.
  sectionBody: {
    paddingLeft: "12px",
    margin: 0,
  },

  sectionTitle: { fontWeight: 700, letterSpacing: 0 },

  dataElementsContainer: {
    gap: "4px",
    padding: isMobile ? "0 14px 12px 30px" : "0 16px 14px 34px",
  },

  dataElementItem: {
    padding: "8px 10px",
    borderRadius: GLASS_RADIUS.row,
    backgroundColor: p.inset,
    minHeight: "36px",
  },

  dataElementText: { fontWeight: 500, letterSpacing: 0 },

  bottomSection: {
    gap: "10px",
    backgroundColor: "transparent",
  },

  // The band around these carries the padding and surface (`parts.footerBand`).
  actionButtonRow: {
    gap: "10px",
    paddingTop: 0,
    backgroundColor: "transparent",
  },

  actionButtonStack: {
    gap: "8px",
    paddingTop: 0,
    backgroundColor: "transparent",
  },

  confirmHint: {
    fontSize: "12px",
    lineHeight: "17px",
    color: textColor,
    opacity: 0.8,
    paddingTop: 0,
    margin: "0 0 10px",
  },

  // The inline verification step sits in the footer band, so it reads as one
  // more panel on the sheet. The base sheet uses the `background` shorthand,
  // which a `backgroundColor` patch would not override.
  otpGatePanel: {
    margin: "0 0 12px",
    padding: "14px",
    background: p.panel,
    border: `1px solid ${p.panelEdge}`,
    borderRadius: GLASS_RADIUS.panel,
  },

  otpGateTitle: { color: headingColor, fontWeight: 700 },

  otpGateDescription: { color: textColor, opacity: 0.85 },

  otpGateDigit: otpField(p, headingColor),

  button: {
    minHeight: isMobile ? "48px" : "44px",
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: 0,
  },

  acceptButton: { boxShadow: p.primaryShadow },

  acceptSecondaryButton: {
    backgroundColor: p.accentWash,
    border: "1px solid transparent",
  },

  rowButton: { padding: "10px 24px" },

  stackButton: { padding: "10px 16px" },

  cancelButton: {
    border: `1px solid ${p.outline}`,
    backgroundColor: "transparent",
    color: textColor,
  },

  // Opaque: it floats over the body's own text, which shows through even a
  // 97% surface — so a translucent host tint is flattened onto white.
  languageDropdown: {
    backgroundColor: opaqueOver(tint, "#ffffff"),
    border: `1px solid ${p.panelEdge}`,
    borderRadius: "16px",
    boxShadow: p.floatShadow,
    minWidth: "140px",
    maxHeight: "240px",
    padding: "6px",
    animation: `${NOTICE_KEYFRAMES.pop} 220ms ${MOTION.outEase} both`,
    transformOrigin: "top right",
  },

  languageItem: {
    color: headingColor,
    backgroundColor: "transparent",
    borderRadius: GLASS_RADIUS.row,
    borderBottom: "none",
  },

  selectedLanguageItem: { backgroundColor: p.accentWash },

  loadingSpinner: {
    width: "18px",
    height: "18px",
    border: `2.5px solid ${p.accentWashStrong}`,
    borderTop: `2.5px solid ${p.accent}`,
    animation: `${NOTICE_KEYFRAMES.spin} 0.9s linear infinite`,
    marginBottom: 0,
    flexShrink: 0,
  },
});

/**
 * Patches the responsive styles, which the modal spreads after `styles` and
 * which would otherwise re-impose classic widths, paddings and type sizes.
 */
const responsivePatch = (isMobile: boolean): Record<string, Style> => ({
  modal: {
    width: isMobile ? "calc(100% - 32px)" : GLASS_DESKTOP_CARD_WIDTH,
    maxWidth: "none",
  },
  title: { fontSize: isMobile ? "19px" : "21px" },
  subTitle: { fontSize: "14px" },
  optionTitle: { fontSize: isMobile ? "14px" : "15px" },
  optionDescription: { fontSize: isMobile ? "12px" : "13px" },
  dataElementText: { fontSize: "13px" },
  privacyText: { fontSize: isMobile ? "13px" : "14px" },
  topRight: { padding: "0 12px", height: "32px" },
  languageItem: { fontSize: "13px", padding: "9px 12px" },
  headerStack: { gap: "12px" },
  button: {
    fontSize: "15px",
    padding: isMobile ? "10px 16px" : "10px 24px",
  },
});

const glassSurface = (p: GlassPalette): LayoutSurface => ({
  accent: p.accent,
  panel: p.panel,
  panelEdge: p.panelEdge,
  hairline: p.hairline,
  outline: p.outline,
  footer: p.footer,
  accentWash: p.accentWash,
  radii: { panel: GLASS_RADIUS.panel, control: GLASS_RADIUS.control },
});

const loaderPillSurface = (p: GlassPalette): Style => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "12px 18px",
  borderRadius: GLASS_RADIUS.pill,
  backgroundColor: p.control,
  ...blur,
  border: `1px solid ${p.panelEdge}`,
  boxShadow: p.floatShadow,
  zIndex: 1000,
  boxSizing: "border-box",
});

const partsFor = (p: GlassPalette, input: GlassSkinInput): GlassParts => ({
  ...buildLayoutParts(glassSurface(p), input),
  loaderPill: input.isMobile
    ? {
        position: "fixed",
        top: "auto",
        left: "0",
        right: "0",
        bottom: "calc(28px + env(safe-area-inset-bottom, 0px))",
        margin: "0 auto",
        width: "fit-content",
        maxWidth: "none",
        transform: "none",
        ...loaderPillSurface(p),
        animation: `${NOTICE_KEYFRAMES.pop} 260ms ${MOTION.outEase} both`,
      }
    : {
        position: "fixed",
        top: "50%",
        left: "50%",
        width: "auto",
        transform: "translate(-50%, -50%)",
        ...loaderPillSurface(p),
        animation: `${NOTICE_KEYFRAMES.floatPill} 260ms ${MOTION.outEase} both`,
      },
  loaderText: {
    margin: 0,
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: 1.2,
  },
  rowClassName: GLASS_ROW_CLASS,
});

const cssVariablesFor = (p: GlassPalette): Record<string, string> => ({
  [GLASS_VARS.accent]: p.accent,
  [GLASS_VARS.accentMid]: withAlpha(p.accent, 0.55),
  [GLASS_VARS.accentFaint]: p.accentFaint,
  [GLASS_VARS.track]: p.accentWashStrong,
  [GLASS_VARS.outline]: p.outline,
  [GLASS_VARS.hairline]: p.hairline,
  [GLASS_VARS.control]: p.control,
});

const controlSkinFor = (
  p: GlassPalette,
  { headingColor }: GlassSkinInput,
): SelectionControlSkin => ({
  accent: p.accent,
  select: {
    border: `1px solid ${p.outline}`,
    borderRadius: GLASS_RADIUS.control,
    backgroundColor: p.control,
    color: headingColor,
  },
});

const confirmSkinFor = (
  p: GlassPalette,
  { headingColor, textColor }: GlassSkinInput,
): ConfirmDialogSkin => ({
  overlay: { backgroundColor: p.dim },
  panel: {
    backgroundColor: p.sheetDense,
    ...blur,
    borderRadius: GLASS_RADIUS.panel,
    border: `1px solid ${p.panelEdge}`,
    boxShadow: p.floatShadow,
    animation: `${NOTICE_KEYFRAMES.pop} 220ms ${MOTION.outEase} both`,
  },
  title: { color: headingColor, fontWeight: 700 },
  detail: { color: textColor },
  button: {
    borderRadius: GLASS_RADIUS.pill,
    border: `1px solid ${p.outline}`,
    backgroundColor: "transparent",
    color: textColor,
    fontWeight: 600,
  },
});

/** Everything the glass appearance draws with, derived from the workspace brand. */
export const buildGlassSkin = <T extends StyleMap>(
  base: T,
  input: GlassSkinInput,
): NoticeSkin<T> => {
  // Resolve once so no builder can reach the raw host value: an invalid or
  // empty tint must fall back everywhere, not only in the palette.
  const resolved: GlassSkinInput = {
    ...input,
    accent: paintableColor(input.accent, DEFAULT_ACCENT_COLOR),
    tint: paintableColor(input.tint, GLASS_DEFAULT_TINT),
  };
  const palette = glassPalette({
    accent: resolved.accent,
    tint: resolved.tint,
  });
  return {
    palette,
    styles: mergeNoticeStyles(base, stylePatch(palette, resolved)),
    responsive: responsivePatch(resolved.isMobile),
    parts: partsFor(palette, resolved),
    cssVariables: cssVariablesFor(palette),
    controlSkin: controlSkinFor(palette, resolved),
    confirmSkin: confirmSkinFor(palette, resolved),
  };
};
